import * as React from 'react';

/**
 * Coral 立宪 — Pill (spec 2.2)
 *
 * Selectable pill, 三态 selected/unselected/disabled × 三 variant brand/discount/neutral.
 *
 * - brand: 规格/属性选项 (主品牌色, 唯一强调态)
 * - discount: 折扣 % pill (业务色 orange — spec 1.2 业务色专属豁免)
 * - neutral: tab/筛选 (中性反相)
 */

export type PillVariant = 'brand' | 'discount' | 'neutral';

export interface PillProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: PillVariant;
  selected?: boolean;
  disabled?: boolean;
}

const SELECTED_CLASS: Record<PillVariant, string> = {
  brand: 'bg-primary-500 text-white hover:bg-primary-600',
  // orange 是 spec 1.2 业务色, foundation/coral/** 已在 ESLint allowlist + foundation 整体豁免
  discount: 'bg-orange-500 text-white hover:bg-orange-600',
  neutral: 'bg-ink-primary text-ink-inverse hover:opacity-90',
};

const UNSELECTED_CLASS =
  'bg-surface-muted text-ink-secondary hover:bg-edge-subtle hover:text-ink-primary';

const DISABLED_CLASS = 'bg-surface-muted text-ink-disabled cursor-not-allowed';

const BASE_CLASS =
  'inline-flex items-center justify-center gap-1.5 ' +
  // Pill renders a <button>, so the touch floor is its own responsibility —
  // same as Button and Modal's close control. Leaving it to callers meant
  // `px-4 py-1.5 text-sm` computed to 32px and every caller had to remember
  // `min-h-[var(--size-touch-rec)]`; 11 did, 3 did not, and AllergenInput's
  // `py-1 text-xs` override pushed its 14 allergen toggles down to 28px.
  // The AST guard cannot catch that: it only matches declared h-* utilities,
  // never a height computed from padding and font size.
  'min-h-[var(--size-touch-rec)] ' +
  'rounded-full px-4 py-1.5 text-sm font-medium ' +
  'transition-colors select-none ' +
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-edge-focus focus-visible:ring-offset-2';

export const Pill: React.FC<PillProps> = ({
  variant = 'brand',
  selected = false,
  disabled = false,
  className,
  children,
  type = 'button',
  ...rest
}) => {
  const stateClass = disabled
    ? DISABLED_CLASS
    : selected
      ? SELECTED_CLASS[variant]
      : UNSELECTED_CLASS;

  const classes = [BASE_CLASS, stateClass, className ?? ''].filter(Boolean).join(' ');

  return (
    <button
      type={type}
      className={classes}
      disabled={disabled}
      aria-pressed={selected}
      data-variant={variant}
      data-selected={selected || undefined}
      {...rest}
    >
      {children}
    </button>
  );
};
